import React, { useState, useEffect, useMemo, useRef } from 'react'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    ListItemText,
    Select,
    MenuItem,
    IconButton,
    Chip,
    Tabs,
    Tab,
    Checkbox,
    Grid,
    Divider,
} from '@material-ui/core'
import {
    Close as CloseIcon,
    Mail as MailIcon,
    Message as MessageSquareIcon,
    Schedule as DelayIcon,
    ExpandMore,
    ExpandLess,
    Add as AddIcon,
} from '@material-ui/icons'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'

import {
    Campaign,
    CampaignStep,
    CampaignStepType,
    CampaignStatus,
} from '../../../../models/Outreach/types'
import { TabPanel } from '../../../../components/TabPanel/TabPanel'
import {
    TIME_OPTIONS,
    WeeklySchedule,
    DaySchedule,
} from '../CreateCampaign/types'
import { Container } from '../../../../components'
import { useGuestCards } from '../../../../hooks/Outreach/useGuestCards'
import { useGuestCardChannelDefaults } from '../../../../hooks/Outreach/useGuestCardChannelDefaults'
import { useConcessions } from '../../../../hooks/useConcessions'
import { CreateGuestCardModal } from '../GuestCards/CreateGuestCardModal'
import { RichTextEmailEditor } from '../CreateCampaign/RichTextEmailEditor'
import { MergeVariableDropdown } from '../CreateCampaign/MergeVariableDropdown'
import { FileAttachments } from '../CreateCampaign/FileAttachments'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            borderRadius: theme.spacing(2),
            maxWidth: '1600px',
            width: '100%',
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
        },
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(3),
        borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
        flexShrink: 0,
        '& .MuiTypography-root': {
            flex: 1,
        },
    },
    dialogContent: {
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: 1,
        minHeight: 0,
    },
    dialogActions: {
        padding: theme.spacing(2, 3),
        borderTop: '1px solid rgba(226, 232, 240, 0.5)',
        flexShrink: 0,
    },
    tabsContainer: {
        backgroundColor: '#ffffff',
        borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
        padding: theme.spacing(0.5),
        margin: 0,
        flexShrink: 0,
    },
    tab: {
        textTransform: 'none',
        fontSize: '0.875rem',
        fontWeight: 500,
        minWidth: 120,
        borderRadius: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        padding: theme.spacing(1, 2),
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.05)',
        },
        '&.Mui-selected': {
            backgroundColor: '#3b82f6',
            color: 'white',
            fontWeight: 600,
        },
    },
    tabContent: {
        padding: theme.spacing(3),
        overflowY: 'auto',
        flex: 1,
        minHeight: 0,
    },
    formField: {
        marginBottom: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '&:hover fieldset': {
                borderColor: '#3b82f6',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: 2,
            },
        },
        '& .MuiInputLabel-root': {
            color: '#6b7280',
            fontSize: '0.875rem',
            fontWeight: 500,
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#3b82f6',
        },
    },
    stepCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: theme.spacing(2),
        borderRadius: '12px',
    },
    stepHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2),
        borderBottom: '1px solid #e2e8f0',
    },
    stepIconContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    stepIcon: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    stepContent: {
        padding: theme.spacing(3),
    },
    stepSidebar: {
        padding: theme.spacing(3),
        borderLeft: '1px solid #e2e8f0',
        backgroundColor: '#f8fafc',
    },
    dayRow: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 0),
        borderBottom: '1px solid #e2e8f0',
        gap: theme.spacing(1),
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    dayLabel: {
        minWidth: 60,
        fontSize: '0.875rem',
        fontWeight: 500,
    },
    timeInput: {
        width: 120,
        '& .MuiOutlinedInput-input': {
            padding: theme.spacing(1),
            fontSize: '0.875rem',
        },
    },
    saveButton: {
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(135deg, #2563eb, #1e40af)',
        },
    },
    cardTitle: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#1f2937',
    },
    cardDescription: {
        color: '#6b7280',
        fontSize: '0.875rem',
        marginTop: theme.spacing(0.5),
    },
    connector: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: theme.spacing(1, 0),
    },
    connectorDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: '#10b981',
        marginBottom: theme.spacing(0.5),
    },
    connectorLine: {
        width: 2,
        height: 20,
        backgroundColor: '#10b981',
    },
    delayCard: {
        border: 'none',
        backgroundColor: '#fef3c7',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: theme.spacing(2),
        borderRadius: '12px',
        padding: theme.spacing(2),
    },
}))

const defaultSchedule: WeeklySchedule = {
    sun: { enabled: false, startTime: '12:00 AM', endTime: '12:00 AM' },
    mon: { enabled: true, startTime: '08:00 AM', endTime: '06:00 PM' },
    tue: { enabled: true, startTime: '08:00 AM', endTime: '06:00 PM' },
    wed: { enabled: true, startTime: '08:00 AM', endTime: '06:00 PM' },
    thu: { enabled: true, startTime: '08:00 AM', endTime: '06:00 PM' },
    fri: { enabled: true, startTime: '08:00 AM', endTime: '06:00 PM' },
    sat: { enabled: false, startTime: '12:00 AM', endTime: '12:00 AM' },
}

interface EditCampaignModalProps {
    open: boolean
    onClose: () => void
    campaign: Campaign | null
    onSave: (updates: Partial<Campaign>, steps: CampaignStep[]) => void
    isSaving?: boolean
}

// Convert backend schedule to frontend WeeklySchedule format
const convertScheduleToWeekly = (
    schedule: CampaignStep['schedule'],
): WeeklySchedule => {
    const weekly: WeeklySchedule = { ...defaultSchedule }

    if (schedule && schedule.length > 0) {
        const dayMap: Record<number, keyof WeeklySchedule> = {
            0: 'sun',
            1: 'mon',
            2: 'tue',
            3: 'wed',
            4: 'thu',
            5: 'fri',
            6: 'sat',
        }

        schedule.forEach((s) => {
            const dayKey = dayMap[s.day_of_week]
            if (dayKey) {
                weekly[dayKey] = {
                    enabled: s.enabled,
                    startTime: s.start_time,
                    endTime: s.end_time,
                }
            }
        })
    }

    return weekly
}

// Convert frontend WeeklySchedule to backend schedule format
const convertWeeklyToSchedule = (
    weekly: WeeklySchedule,
): CampaignStep['schedule'] => {
    const dayMap: Record<keyof WeeklySchedule, number> = {
        sun: 0,
        mon: 1,
        tue: 2,
        wed: 3,
        thu: 4,
        fri: 5,
        sat: 6,
    }

    return Object.entries(weekly).map(([day, schedule]) => ({
        id: 0, // Will be set by backend
        step: 0, // Will be set by backend
        day_of_week: dayMap[day as keyof WeeklySchedule],
        day_of_week_display: day.charAt(0).toUpperCase() + day.slice(1),
        enabled: schedule.enabled,
        start_time: schedule.startTime,
        end_time: schedule.endTime,
        created_date: '',
        modified_date: '',
    }))
}

export const EditCampaignModal: React.FC<EditCampaignModalProps> = ({
    open,
    onClose,
    campaign,
    onSave,
    isSaving = false,
}) => {
    const classes = useStyles()
    const theme = useTheme()
    const { guestCards } = useGuestCards()
    const { defaults: guestCardDefaults } = useGuestCardChannelDefaults()
    const defaultCampaignGuestCardId = guestCardDefaults.campaign
    const sortedGuestCards = useMemo(() => {
        const cards = guestCards ?? []
        if (defaultCampaignGuestCardId == null) return cards
        return [...cards].sort((a, b) => {
            if (a.id === defaultCampaignGuestCardId) return -1
            if (b.id === defaultCampaignGuestCardId) return 1
            return 0
        })
    }, [guestCards, defaultCampaignGuestCardId])
    const { concessions } = useConcessions()
    const [activeTab, setActiveTab] = useState(0)
    const [campaignName, setCampaignName] = useState('')
    const [campaignStatus, setCampaignStatus] = useState<CampaignStatus>(
        CampaignStatus.ACTIVE,
    )
    const [campaignGoal, setCampaignGoal] = useState<string>('')
    const [replyToEmail, setReplyToEmail] = useState<string>('')
    const [senderName, setSenderName] = useState<string>('')
    const [concessionIds, setConcessionIds] = useState<number[]>([])
    const [steps, setSteps] = useState<CampaignStep[]>([])
    const [collapsedSteps, setCollapsedSteps] = useState<Set<number>>(new Set())
    const [createGuestCardModalOpen, setCreateGuestCardModalOpen] = useState(
        false,
    )
    const [currentStepForGuestCard, setCurrentStepForGuestCard] = useState<
        number | null
    >(null)

    const editorInsertRefs = useRef<Record<number, (text: string) => void>>({})

    useEffect(() => {
        if (campaign) {
            setCampaignName(campaign.name || '')
            setCampaignStatus(campaign.status || CampaignStatus.ACTIVE)
            setCampaignGoal(campaign.campaign_goal || '')
            setReplyToEmail(campaign.reply_to_email || '')
            setSenderName(campaign.sender_name || '')
            setConcessionIds(campaign.concessions ?? [])
            // Sort steps by step_order to ensure correct order
            const sortedSteps = campaign.steps
                ? [...campaign.steps].sort(
                      (a, b) => a.step_order - b.step_order,
                  )
                : []
            setSteps(sortedSteps)
            // All steps expanded by default
            setCollapsedSteps(new Set())
        }
    }, [campaign, open])

    // Validate that all message steps have a guest card
    const messageStepsForValidation = steps.filter(
        (step) =>
            step.step_type === CampaignStepType.EMAIL ||
            step.step_type === CampaignStepType.TEXT_MESSAGE,
    )
    const stepsWithoutGuestCard = messageStepsForValidation.filter(
        (step) => !step.guest_card,
    )
    const isValid =
        messageStepsForValidation.length === 0 ||
        stepsWithoutGuestCard.length === 0

    const handleSave = () => {
        if (!campaign) return

        const updates: Partial<Campaign> = {
            name: campaignName,
            status: campaignStatus,
            campaign_goal: campaignGoal || undefined,
            reply_to_email: replyToEmail,
            sender_name: senderName,
            concessions: concessionIds,
        }

        onSave(updates, steps)
    }

    const handleStepChange = (
        stepIndex: number,
        field: keyof CampaignStep,
        value: any,
    ) => {
        const updatedSteps = [...steps]
        updatedSteps[stepIndex] = {
            ...updatedSteps[stepIndex],
            [field]: value,
        }
        setSteps(updatedSteps)
    }

    const handleStepTypeChange = (
        stepIndex: number,
        newType: CampaignStepType,
    ) => {
        const updatedSteps = [...steps]
        const step = updatedSteps[stepIndex]

        updatedSteps[stepIndex] = {
            ...step,
            step_type: newType,
            // Remove subject if changing from EMAIL to TEXT_MESSAGE
            subject:
                newType === CampaignStepType.TEXT_MESSAGE ? null : step.subject,
        }
        setSteps(updatedSteps)
    }

    const handleScheduleChange = (
        stepIndex: number,
        day: keyof WeeklySchedule,
        field: keyof DaySchedule,
        value: boolean | string,
    ) => {
        const updatedSteps = [...steps]
        const step = updatedSteps[stepIndex]

        // Convert backend schedule to weekly format
        const weeklySchedule = convertScheduleToWeekly(step.schedule)

        // Update the day schedule
        weeklySchedule[day] = {
            ...weeklySchedule[day],
            [field]: value,
        }

        // Convert back to backend format
        const updatedSchedule = convertWeeklyToSchedule(weeklySchedule)

        updatedSteps[stepIndex] = {
            ...step,
            schedule: updatedSchedule,
        }
        setSteps(updatedSteps)
    }

    const handleToggleExpand = (stepId: number) => {
        setCollapsedSteps((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(stepId)) {
                newSet.delete(stepId)
            } else {
                newSet.add(stepId)
            }
            return newSet
        })
    }

    const getStepIcon = (stepType: CampaignStepType) => {
        switch (stepType) {
            case CampaignStepType.EMAIL:
                return MailIcon
            case CampaignStepType.TEXT_MESSAGE:
                return MessageSquareIcon
            default:
                return MailIcon
        }
    }

    const getStepColor = (stepType: CampaignStepType) => {
        switch (stepType) {
            case CampaignStepType.EMAIL:
                return '#3b82f6'
            case CampaignStepType.TEXT_MESSAGE:
                return '#10b981'
            default:
                return '#3b82f6'
        }
    }

    const getStepTypeLabel = (stepType: CampaignStepType) => {
        switch (stepType) {
            case CampaignStepType.EMAIL:
                return 'Email'
            case CampaignStepType.TEXT_MESSAGE:
                return 'SMS'
            case CampaignStepType.DELAY:
                return 'Delay'
            default:
                return stepType
        }
    }

    if (!campaign) return null

    // Sort all steps by step_order
    const sortedSteps = [...steps].sort((a, b) => a.step_order - b.step_order)

    // Helper to check if step is a delay
    const isDelayStep = (step: CampaignStep) =>
        step.step_type === CampaignStepType.DELAY

    // Helper to check if step is a message step
    const isMessageStep = (step: CampaignStep) =>
        step.step_type === CampaignStepType.EMAIL ||
        step.step_type === CampaignStepType.TEXT_MESSAGE

    // Get message steps for numbering
    const messageSteps = sortedSteps.filter(isMessageStep)

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.dialog}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle className={classes.dialogTitle}>
                <Container style={{ flex: 1 }}>
                    <Typography
                        variant="h6"
                        style={{
                            fontWeight: 'bold',
                            color: '#1f2937',
                            margin: 0,
                        }}
                    >
                        Edit Campaign
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        style={{ marginLeft: 'auto', padding: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Container>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                {/* Tabs */}
                <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    className={classes.tabsContainer}
                    TabIndicatorProps={{ style: { display: 'none' } }}
                    variant="fullWidth"
                >
                    <Tab label="Message Sequence" className={classes.tab} />
                    <Tab label="Campaign Setup" className={classes.tab} />
                </Tabs>

                {/* Message Sequence Tab */}
                <TabPanel
                    value={activeTab}
                    index={0}
                    style={{ flexDirection: 'column', flex: 1, minHeight: 0 }}
                >
                    <Box className={classes.tabContent}>
                        {sortedSteps.length === 0 ? (
                            <Card
                                style={{
                                    border: 'none',
                                    backgroundColor: '#ffffff',
                                    boxShadow:
                                        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                    padding: theme.spacing(4),
                                    textAlign: 'center',
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    color="textSecondary"
                                >
                                    No steps found
                                </Typography>
                            </Card>
                        ) : (
                            sortedSteps.map((step, index) => {
                                // Handle delay steps
                                if (isDelayStep(step)) {
                                    const stepIndex = steps.findIndex(
                                        (s) => s.id === step.id,
                                    )
                                    return (
                                        <Box key={step.id}>
                                            {index > 0 && (
                                                <Box
                                                    className={
                                                        classes.connector
                                                    }
                                                >
                                                    <Box
                                                        className={
                                                            classes.connectorDot
                                                        }
                                                    />
                                                    <Box
                                                        className={
                                                            classes.connectorLine
                                                        }
                                                    />
                                                </Box>
                                            )}
                                            <Card className={classes.delayCard}>
                                                <CardContent>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                    >
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            style={{ gap: 12 }}
                                                        >
                                                            <DelayIcon
                                                                style={{
                                                                    color:
                                                                        '#f59e0b',
                                                                    fontSize: 24,
                                                                }}
                                                            />
                                                            <Box>
                                                                <Typography
                                                                    variant="body1"
                                                                    style={{
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    Wait{' '}
                                                                    {step.delay_days ||
                                                                        0}{' '}
                                                                    {(step.delay_days ||
                                                                        0) === 1
                                                                        ? 'day'
                                                                        : 'days'}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="textSecondary"
                                                                >
                                                                    Delay before
                                                                    next step
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            style={{ gap: 8 }}
                                                        >
                                                            <TextField
                                                                type="number"
                                                                value={
                                                                    step.delay_days ||
                                                                    0
                                                                }
                                                                onChange={(
                                                                    e: React.ChangeEvent<HTMLInputElement>,
                                                                ) => {
                                                                    handleStepChange(
                                                                        stepIndex,
                                                                        'delay_days',
                                                                        parseInt(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        ) || 0,
                                                                    )
                                                                }}
                                                                inputProps={{
                                                                    min: 0,
                                                                    style: {
                                                                        padding: 8,
                                                                        width: 60,
                                                                    },
                                                                }}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    )
                                }

                                // Handle message steps
                                const stepIndex = steps.findIndex(
                                    (s) => s.id === step.id,
                                )
                                const StepIcon = getStepIcon(step.step_type)
                                const stepColor = getStepColor(step.step_type)
                                const isExpanded = !collapsedSteps.has(step.id)
                                // Calculate step number by counting only message steps up to this point
                                const stepNumber =
                                    sortedSteps
                                        .slice(0, index + 1)
                                        .filter(isMessageStep)
                                        .findIndex((s) => s.id === step.id) + 1
                                const weeklySchedule = convertScheduleToWeekly(
                                    step.schedule,
                                )

                                return (
                                    <Box key={step.id}>
                                        {index > 0 && (
                                            <Box className={classes.connector}>
                                                <Box
                                                    className={
                                                        classes.connectorDot
                                                    }
                                                />
                                                <Box
                                                    className={
                                                        classes.connectorLine
                                                    }
                                                />
                                            </Box>
                                        )}

                                        <Card className={classes.stepCard}>
                                            {/* Step Header */}
                                            <Box className={classes.stepHeader}>
                                                <Box
                                                    className={
                                                        classes.stepIconContainer
                                                    }
                                                >
                                                    <Box
                                                        className={
                                                            classes.stepIcon
                                                        }
                                                        style={{
                                                            backgroundColor: `${stepColor}15`,
                                                        }}
                                                    >
                                                        <StepIcon
                                                            style={{
                                                                color: stepColor,
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box>
                                                        <Typography
                                                            variant="body1"
                                                            style={{
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {step.step_type ===
                                                            CampaignStepType.EMAIL
                                                                ? 'EMAIL'
                                                                : 'SMS'}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                        >
                                                            Step {stepNumber}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    style={{ gap: 8 }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleToggleExpand(
                                                                step.id,
                                                            )
                                                        }
                                                        style={{
                                                            color: '#6b7280',
                                                        }}
                                                    >
                                                        {isExpanded ? (
                                                            <ExpandLess />
                                                        ) : (
                                                            <ExpandMore />
                                                        )}
                                                    </IconButton>
                                                </Box>
                                            </Box>

                                            {/* Expanded Content */}
                                            {isExpanded && (
                                                <Grid container>
                                                    <Grid item xs={12} md={7}>
                                                        <Box
                                                            className={
                                                                classes.stepContent
                                                            }
                                                        >
                                                            {/* Step Type Selector */}
                                                            <FormControl
                                                                fullWidth
                                                                className={
                                                                    classes.formField
                                                                }
                                                                variant="outlined"
                                                            >
                                                                <InputLabel>
                                                                    Change step
                                                                </InputLabel>
                                                                <Select
                                                                    value={
                                                                        step.step_type
                                                                    }
                                                                    onChange={(
                                                                        e: React.ChangeEvent<{
                                                                            value: unknown
                                                                        }>,
                                                                    ) => {
                                                                        handleStepTypeChange(
                                                                            stepIndex,
                                                                            e
                                                                                .target
                                                                                .value as CampaignStepType,
                                                                        )
                                                                    }}
                                                                    label="Change step"
                                                                >
                                                                    <MenuItem
                                                                        value={
                                                                            CampaignStepType.EMAIL
                                                                        }
                                                                    >
                                                                        Email
                                                                    </MenuItem>
                                                                    <MenuItem
                                                                        value={
                                                                            CampaignStepType.TEXT_MESSAGE
                                                                        }
                                                                    >
                                                                        SMS
                                                                    </MenuItem>
                                                                </Select>
                                                            </FormControl>

                                                            {/* Subject (only for email) */}
                                                            {step.step_type ===
                                                                CampaignStepType.EMAIL && (
                                                                <Box
                                                                    style={{
                                                                        position:
                                                                            'relative',
                                                                    }}
                                                                >
                                                                    <TextField
                                                                        fullWidth
                                                                        label="Subject"
                                                                        placeholder="Leave the subject blank to follow up in the same thread"
                                                                        value={
                                                                            step.subject ||
                                                                            ''
                                                                        }
                                                                        onChange={(
                                                                            e: React.ChangeEvent<HTMLInputElement>,
                                                                        ) => {
                                                                            handleStepChange(
                                                                                stepIndex,
                                                                                'subject',
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                        }}
                                                                        className={
                                                                            classes.formField
                                                                        }
                                                                        variant="outlined"
                                                                        InputProps={{
                                                                            endAdornment: (
                                                                                <MergeVariableDropdown
                                                                                    onSelect={(
                                                                                        variable,
                                                                                    ) => {
                                                                                        handleStepChange(
                                                                                            stepIndex,
                                                                                            'subject',
                                                                                            (step.subject ||
                                                                                                '') +
                                                                                                variable,
                                                                                        )
                                                                                    }}
                                                                                />
                                                                            ),
                                                                        }}
                                                                    />
                                                                </Box>
                                                            )}

                                                            {/* Message Content */}
                                                            {step.step_type ===
                                                            CampaignStepType.EMAIL ? (
                                                                <Box
                                                                    className={
                                                                        classes.formField
                                                                    }
                                                                >
                                                                    <RichTextEmailEditor
                                                                        value={
                                                                            step.content
                                                                        }
                                                                        htmlValue={
                                                                            step.content_html ||
                                                                            undefined
                                                                        }
                                                                        onChange={(
                                                                            plainText,
                                                                            html,
                                                                        ) => {
                                                                            const updatedSteps = [
                                                                                ...steps,
                                                                            ]
                                                                            updatedSteps[
                                                                                stepIndex
                                                                            ] = {
                                                                                ...updatedSteps[
                                                                                    stepIndex
                                                                                ],
                                                                                content: plainText,
                                                                                content_html: html,
                                                                            }
                                                                            setSteps(
                                                                                updatedSteps,
                                                                            )
                                                                        }}
                                                                        onInsertRef={(
                                                                            insertFn,
                                                                        ) => {
                                                                            editorInsertRefs.current[
                                                                                step.id
                                                                            ] = insertFn
                                                                        }}
                                                                        toolbarExtra={
                                                                            <MergeVariableDropdown
                                                                                onSelect={(
                                                                                    variable,
                                                                                ) => {
                                                                                    const insertFn =
                                                                                        editorInsertRefs
                                                                                            .current[
                                                                                            step
                                                                                                .id
                                                                                        ]
                                                                                    if (
                                                                                        insertFn
                                                                                    ) {
                                                                                        insertFn(
                                                                                            variable,
                                                                                        )
                                                                                    }
                                                                                }}
                                                                            />
                                                                        }
                                                                    />
                                                                </Box>
                                                            ) : (
                                                                <TextField
                                                                    fullWidth
                                                                    label="Message"
                                                                    multiline
                                                                    rows={8}
                                                                    placeholder="Type in your follow-up or generate it with AI"
                                                                    value={
                                                                        step.content ||
                                                                        ''
                                                                    }
                                                                    onChange={(
                                                                        e: React.ChangeEvent<HTMLInputElement>,
                                                                    ) => {
                                                                        handleStepChange(
                                                                            stepIndex,
                                                                            'content',
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        )
                                                                    }}
                                                                    className={
                                                                        classes.formField
                                                                    }
                                                                    variant="outlined"
                                                                />
                                                            )}

                                                            {/* Attachments (email only) */}
                                                            {step.step_type ===
                                                                CampaignStepType.EMAIL && (
                                                                <Box
                                                                    style={{
                                                                        marginBottom: theme.spacing(
                                                                            2,
                                                                        ),
                                                                    }}
                                                                >
                                                                    <FileAttachments
                                                                        mode="api"
                                                                        campaignId={
                                                                            campaign!
                                                                                .id
                                                                        }
                                                                        stepId={
                                                                            step.id
                                                                        }
                                                                        attachments={
                                                                            step.attachments ||
                                                                            []
                                                                        }
                                                                        onAttachmentsChange={(
                                                                            newAttachments,
                                                                        ) => {
                                                                            const updatedSteps = [
                                                                                ...steps,
                                                                            ]
                                                                            updatedSteps[
                                                                                stepIndex
                                                                            ] = {
                                                                                ...updatedSteps[
                                                                                    stepIndex
                                                                                ],
                                                                                attachments: newAttachments,
                                                                            }
                                                                            setSteps(
                                                                                updatedSteps,
                                                                            )
                                                                        }}
                                                                    />
                                                                </Box>
                                                            )}

                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                            >
                                                                The signature
                                                                will be added
                                                                automatically
                                                                from
                                                                sender&apos;s
                                                                email account.
                                                            </Typography>

                                                            {/* Guest Card Selector */}
                                                            <Box
                                                                display="flex"
                                                                alignItems="flex-start"
                                                                style={{
                                                                    marginTop: theme.spacing(
                                                                        2,
                                                                    ),
                                                                    gap: 8,
                                                                }}
                                                            >
                                                                <FormControl
                                                                    fullWidth
                                                                    className={
                                                                        classes.formField
                                                                    }
                                                                    variant="outlined"
                                                                    style={{
                                                                        marginBottom: 0,
                                                                    }}
                                                                    required
                                                                    error={
                                                                        !step.guest_card
                                                                    }
                                                                >
                                                                    <InputLabel>
                                                                        Attach
                                                                        Guest
                                                                        Card
                                                                        (Required)
                                                                    </InputLabel>
                                                                    <Select
                                                                        value={
                                                                            step
                                                                                .guest_card
                                                                                ?.id ||
                                                                            ''
                                                                        }
                                                                        onChange={(
                                                                            e: React.ChangeEvent<{
                                                                                value: unknown
                                                                            }>,
                                                                        ) => {
                                                                            const value =
                                                                                e
                                                                                    .target
                                                                                    .value
                                                                            if (
                                                                                value ===
                                                                                'CREATE_NEW'
                                                                            ) {
                                                                                setCurrentStepForGuestCard(
                                                                                    step.id,
                                                                                )
                                                                                setCreateGuestCardModalOpen(
                                                                                    true,
                                                                                )
                                                                                // Don't update the step value, so the Select reverts to previous value
                                                                                return
                                                                            }
                                                                            handleStepChange(
                                                                                stepIndex,
                                                                                'guest_card',
                                                                                value
                                                                                    ? (value as number)
                                                                                    : null,
                                                                            )
                                                                        }}
                                                                        label="Attach Guest Card (Required)"
                                                                    >
                                                                        {sortedGuestCards.map(
                                                                            (
                                                                                card,
                                                                            ) => (
                                                                                <MenuItem
                                                                                    key={
                                                                                        card.id
                                                                                    }
                                                                                    value={
                                                                                        card.id
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        card.card_name
                                                                                    }
                                                                                    {card.id ===
                                                                                    defaultCampaignGuestCardId
                                                                                        ? ' (season default)'
                                                                                        : ''}
                                                                                </MenuItem>
                                                                            ),
                                                                        )}
                                                                        <Divider />
                                                                        <MenuItem
                                                                            value="CREATE_NEW"
                                                                            style={{
                                                                                color:
                                                                                    '#3b82f6',
                                                                                fontWeight: 500,
                                                                            }}
                                                                        >
                                                                            <AddIcon
                                                                                style={{
                                                                                    marginRight: 8,
                                                                                    fontSize: 18,
                                                                                }}
                                                                            />
                                                                            Create
                                                                            New
                                                                            Guest
                                                                            Card
                                                                        </MenuItem>
                                                                    </Select>
                                                                    {!step.guest_card && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="error"
                                                                            style={{
                                                                                marginTop: 4,
                                                                                marginLeft: 14,
                                                                            }}
                                                                        >
                                                                            A
                                                                            guest
                                                                            card
                                                                            is
                                                                            required
                                                                            for
                                                                            this
                                                                            step
                                                                        </Typography>
                                                                    )}
                                                                </FormControl>
                                                            </Box>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12} md={5}>
                                                        <Box
                                                            className={
                                                                classes.stepSidebar
                                                            }
                                                        >
                                                            <Typography
                                                                variant="subtitle2"
                                                                gutterBottom
                                                                style={{
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                Schedule
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                                paragraph
                                                            >
                                                                Select days and
                                                                times when this
                                                                step should
                                                                execute
                                                            </Typography>

                                                            <Divider
                                                                style={{
                                                                    marginBottom: 16,
                                                                }}
                                                            />

                                                            {([
                                                                'sun',
                                                                'mon',
                                                                'tue',
                                                                'wed',
                                                                'thu',
                                                                'fri',
                                                                'sat',
                                                            ] as const).map(
                                                                (day) => {
                                                                    const daySchedule =
                                                                        weeklySchedule[
                                                                            day
                                                                        ]
                                                                    const dayLabels = {
                                                                        sun:
                                                                            'SUN',
                                                                        mon:
                                                                            'MON',
                                                                        tue:
                                                                            'TUE',
                                                                        wed:
                                                                            'WED',
                                                                        thu:
                                                                            'THU',
                                                                        fri:
                                                                            'FRI',
                                                                        sat:
                                                                            'SAT',
                                                                    }

                                                                    return (
                                                                        <Box
                                                                            key={
                                                                                day
                                                                            }
                                                                            className={
                                                                                classes.dayRow
                                                                            }
                                                                        >
                                                                            <Checkbox
                                                                                checked={
                                                                                    daySchedule.enabled
                                                                                }
                                                                                onChange={(
                                                                                    e,
                                                                                ) =>
                                                                                    handleScheduleChange(
                                                                                        stepIndex,
                                                                                        day,
                                                                                        'enabled',
                                                                                        e
                                                                                            .target
                                                                                            .checked,
                                                                                    )
                                                                                }
                                                                                size="small"
                                                                                style={{
                                                                                    padding: 4,
                                                                                }}
                                                                            />
                                                                            <Typography
                                                                                className={
                                                                                    classes.dayLabel
                                                                                }
                                                                            >
                                                                                {
                                                                                    dayLabels[
                                                                                        day
                                                                                    ]
                                                                                }
                                                                            </Typography>
                                                                            {daySchedule.enabled ? (
                                                                                <>
                                                                                    <FormControl
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        className={
                                                                                            classes.timeInput
                                                                                        }
                                                                                    >
                                                                                        <Select
                                                                                            value={
                                                                                                daySchedule.startTime
                                                                                            }
                                                                                            onChange={(
                                                                                                e: React.ChangeEvent<{
                                                                                                    value: unknown
                                                                                                }>,
                                                                                            ) =>
                                                                                                handleScheduleChange(
                                                                                                    stepIndex,
                                                                                                    day,
                                                                                                    'startTime',
                                                                                                    e
                                                                                                        .target
                                                                                                        .value as string,
                                                                                                )
                                                                                            }
                                                                                            style={{
                                                                                                fontSize:
                                                                                                    '0.75rem',
                                                                                            }}
                                                                                        >
                                                                                            {TIME_OPTIONS.map(
                                                                                                (
                                                                                                    time,
                                                                                                ) => (
                                                                                                    <MenuItem
                                                                                                        key={
                                                                                                            time
                                                                                                        }
                                                                                                        value={
                                                                                                            time
                                                                                                        }
                                                                                                    >
                                                                                                        {
                                                                                                            time
                                                                                                        }
                                                                                                    </MenuItem>
                                                                                                ),
                                                                                            )}
                                                                                        </Select>
                                                                                    </FormControl>
                                                                                    <Typography variant="caption">
                                                                                        -
                                                                                    </Typography>
                                                                                    <FormControl
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        className={
                                                                                            classes.timeInput
                                                                                        }
                                                                                    >
                                                                                        <Select
                                                                                            value={
                                                                                                daySchedule.endTime
                                                                                            }
                                                                                            onChange={(
                                                                                                e: React.ChangeEvent<{
                                                                                                    value: unknown
                                                                                                }>,
                                                                                            ) =>
                                                                                                handleScheduleChange(
                                                                                                    stepIndex,
                                                                                                    day,
                                                                                                    'endTime',
                                                                                                    e
                                                                                                        .target
                                                                                                        .value as string,
                                                                                                )
                                                                                            }
                                                                                            style={{
                                                                                                fontSize:
                                                                                                    '0.75rem',
                                                                                            }}
                                                                                        >
                                                                                            {TIME_OPTIONS.map(
                                                                                                (
                                                                                                    time,
                                                                                                ) => (
                                                                                                    <MenuItem
                                                                                                        key={
                                                                                                            time
                                                                                                        }
                                                                                                        value={
                                                                                                            time
                                                                                                        }
                                                                                                    >
                                                                                                        {
                                                                                                            time
                                                                                                        }
                                                                                                    </MenuItem>
                                                                                                ),
                                                                                            )}
                                                                                        </Select>
                                                                                    </FormControl>
                                                                                </>
                                                                            ) : (
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    color="textSecondary"
                                                                                    style={{
                                                                                        fontSize:
                                                                                            '0.75rem',
                                                                                    }}
                                                                                >
                                                                                    12:00
                                                                                    AM
                                                                                    -
                                                                                    12:00
                                                                                    AM
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    )
                                                                },
                                                            )}
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            )}

                                            {/* Collapsed Preview */}
                                            {!isExpanded && (
                                                <CardContent>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                    >
                                                        <Box>
                                                            {step.subject && (
                                                                <Typography
                                                                    variant="body2"
                                                                    style={{
                                                                        fontWeight: 500,
                                                                        marginBottom: 4,
                                                                    }}
                                                                >
                                                                    {
                                                                        step.subject
                                                                    }
                                                                </Typography>
                                                            )}
                                                            {step.content && (
                                                                <Typography
                                                                    variant="body2"
                                                                    color="textSecondary"
                                                                >
                                                                    {step.content.substring(
                                                                        0,
                                                                        100,
                                                                    )}
                                                                    {step
                                                                        .content
                                                                        .length >
                                                                    100
                                                                        ? '...'
                                                                        : ''}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            )}
                                        </Card>
                                    </Box>
                                )
                            })
                        )}
                    </Box>
                </TabPanel>

                {/* Campaign Setup Tab */}
                <TabPanel
                    value={activeTab}
                    index={1}
                    style={{ flexDirection: 'column', flex: 1, minHeight: 0 }}
                >
                    <Box className={classes.tabContent}>
                        <Card
                            style={{
                                border: 'none',
                                backgroundColor: '#ffffff',
                                boxShadow:
                                    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            }}
                        >
                            <Box style={{ padding: '24px 24px 16px 24px' }}>
                                <Typography className={classes.cardTitle}>
                                    Campaign Details
                                </Typography>
                                <Typography className={classes.cardDescription}>
                                    Update your campaign name, status, and goal
                                </Typography>
                            </Box>
                            <CardContent
                                style={{ padding: '0 24px 24px 24px' }}
                            >
                                <TextField
                                    fullWidth
                                    label="Campaign Name"
                                    value={campaignName}
                                    onChange={(e) =>
                                        setCampaignName(e.target.value)
                                    }
                                    className={classes.formField}
                                    variant="outlined"
                                />
                                <TextField
                                    fullWidth
                                    label="Reply-To Email"
                                    placeholder="e.g., leasing@oakwoodapartments.com"
                                    value={replyToEmail}
                                    onChange={(e) =>
                                        setReplyToEmail(e.target.value)
                                    }
                                    className={classes.formField}
                                    variant="outlined"
                                    type="email"
                                    required
                                />
                                <TextField
                                    fullWidth
                                    label="Sender Name"
                                    placeholder="e.g., Sarah from Oakwood Apartments"
                                    value={senderName}
                                    onChange={(e) =>
                                        setSenderName(e.target.value)
                                    }
                                    className={classes.formField}
                                    variant="outlined"
                                    required
                                />
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    className={classes.formField}
                                >
                                    <InputLabel>Campaign Goal</InputLabel>
                                    <Select
                                        value={campaignGoal}
                                        onChange={(e) =>
                                            setCampaignGoal(
                                                e.target.value as string,
                                            )
                                        }
                                        label="Campaign Goal"
                                    >
                                        <MenuItem value="schedule-tour">
                                            Schedule a Tour
                                        </MenuItem>
                                        <MenuItem value="request-info">
                                            Request More Info
                                        </MenuItem>
                                        <MenuItem value="apply-now">
                                            Apply Now
                                        </MenuItem>
                                        <MenuItem value="call-us">
                                            Call Us Today
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl
                                    variant="outlined"
                                    fullWidth
                                    className={classes.formField}
                                >
                                    <InputLabel
                                        id="edit-campaign-concession-label"
                                        shrink={concessionIds.length > 0}
                                        style={
                                            concessionIds.length === 0
                                                ? {
                                                      transform:
                                                          'translate(14px, 12px) scale(1)',
                                                  }
                                                : undefined
                                        }
                                    >
                                        Concessions (optional)
                                    </InputLabel>
                                    <Select
                                        labelId="edit-campaign-concession-label"
                                        multiple
                                        value={concessionIds}
                                        onChange={(e) =>
                                            setConcessionIds(
                                                e.target.value as number[],
                                            )
                                        }
                                        label={
                                            concessionIds.length > 0
                                                ? 'Concessions (optional)'
                                                : undefined
                                        }
                                        renderValue={(selected) => {
                                            const ids = selected as number[]
                                            if (ids.length === 0) return <></>
                                            return (
                                                <Box
                                                    display="flex"
                                                    flexWrap="nowrap"
                                                    alignItems="center"
                                                    style={{
                                                        gap: 4,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {ids.map((id) => {
                                                        const c = concessions.find(
                                                            (x) => x.id === id,
                                                        )
                                                        return (
                                                            <Chip
                                                                key={id}
                                                                label={
                                                                    c?.name ??
                                                                    id
                                                                }
                                                                size="small"
                                                                style={{
                                                                    height: 20,
                                                                    fontSize:
                                                                        '0.675rem',
                                                                    fontWeight: 600,
                                                                    backgroundColor:
                                                                        '#d1fae5',
                                                                    color:
                                                                        '#047857',
                                                                    borderRadius: 6,
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                        )
                                                    })}
                                                </Box>
                                            )
                                        }}
                                        style={{ maxHeight: 40 }}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                    marginTop: 4,
                                                },
                                            },
                                            anchorOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            },
                                            transformOrigin: {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            },
                                            getContentAnchorEl: null,
                                        }}
                                    >
                                        {concessions
                                            .filter((c) => c.is_active)
                                            .map((c) => (
                                                <MenuItem
                                                    key={c.id}
                                                    value={c.id}
                                                    style={{
                                                        paddingTop: 6,
                                                        paddingBottom: 6,
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={concessionIds.includes(
                                                            c.id,
                                                        )}
                                                        size="small"
                                                        style={{
                                                            color: '#047857',
                                                            padding:
                                                                '0 8px 0 0',
                                                        }}
                                                    />
                                                    <ListItemText
                                                        primary={c.name}
                                                        primaryTypographyProps={{
                                                            variant: 'body2',
                                                            style: {
                                                                fontWeight: 500,
                                                            },
                                                        }}
                                                    />
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    className={classes.formField}
                                >
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={campaignStatus}
                                        onChange={(e) =>
                                            setCampaignStatus(
                                                e.target
                                                    .value as CampaignStatus,
                                            )
                                        }
                                        label="Status"
                                    >
                                        <MenuItem value={CampaignStatus.DRAFT}>
                                            Draft
                                        </MenuItem>
                                        <MenuItem value={CampaignStatus.ACTIVE}>
                                            Active
                                        </MenuItem>
                                        <MenuItem value={CampaignStatus.PAUSED}>
                                            Paused
                                        </MenuItem>
                                        <MenuItem
                                            value={CampaignStatus.ARCHIVED}
                                        >
                                            Archived
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </CardContent>
                        </Card>
                    </Box>
                </TabPanel>
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    className={classes.saveButton}
                    disabled={
                        isSaving ||
                        !campaignName.trim() ||
                        !replyToEmail.trim() ||
                        !senderName.trim() ||
                        !isValid
                    }
                >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>

            {/* Create Guest Card Modal */}
            <CreateGuestCardModal
                open={createGuestCardModalOpen}
                onClose={() => {
                    setCreateGuestCardModalOpen(false)
                    setCurrentStepForGuestCard(null)
                }}
                onGuestCardCreated={(guestCardId) => {
                    if (currentStepForGuestCard !== null) {
                        const stepIndex = steps.findIndex(
                            (s) => s.id === currentStepForGuestCard,
                        )
                        if (stepIndex !== -1) {
                            handleStepChange(
                                stepIndex,
                                'guest_card',
                                guestCardId,
                            )
                        }
                    }
                    setCreateGuestCardModalOpen(false)
                    setCurrentStepForGuestCard(null)
                }}
            />
        </Dialog>
    )
}
